import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setToken } from "@/lib/api";
import { ArrowLeft, ArrowRight, Brain, Building2, ChevronDown, ChevronUp, HeartHandshake, Stethoscope, X } from "lucide-react";

/* Investor-demo overlay: a "fictional data" banner plus a floating guide
 * card that explains each section and who it benefits, with a linear tour.
 * Active only when localStorage ml_demo_mode === "1". */

export const DEMO_STOPS = [
    {
        path: "/dashboard", title: "Today — Sarah's home base",
        what: "One calm page that answers 'what matters today?': priority actions, check-ins, and gentle guidance instead of 40 open browser tabs.",
        caregiver: "Starts the day with clarity instead of overwhelm.",
        clinician: "Patients arrive with a caregiver who's organized, not exhausted.",
        org: "Daily engagement — the habit loop every care program wants.",
        patient: "A steadier, less stressed caregiver means calmer days for Maggie.",
    },
    {
        path: "/roadmap", title: "The Roadmap — 24/7 personalized guidance",
        what: "Built from Sarah's own inputs (stage, behaviors, check-ins), the Roadmap shows what to prepare for now, what to look for (early warning signs), what may be disease progression vs. what needs a clinician's look — and it anticipates the next need and points Sarah to it BEFORE the crisis. Care team and upcoming events feed the same picture.",
        caregiver: "Replaces 3am doom-Googling with 'here's what's coming and what to do about it'.",
        clinician: "Concerns arrive triaged: 'progression' vs 'get this looked at now'.",
        org: "Anticipatory guidance is the retention spine across the multi-year journey.",
        patient: "Transitions (driving, moving, hospice) happen with planning and dignity, not in crisis.",
    },
    {
        path: "/journey/about", title: "Patient profile — the caregiver-owned record",
        what: "Maggie's demographics, allergies, medications, insurance, care team, and emergency contacts — entered once, kept alive, owned by the family.",
        caregiver: "Never starts from zero when a new provider asks 'so, what's been going on?'",
        clinician: "Accurate meds and history in seconds, not a 20-minute intake.",
        org: "Cleaner referrals and intake data for every family they serve.",
        patient: "Her story travels with her — accurately, with dignity.",
    },
    {
        path: "/journey/documents", title: "Care records — AI does the filing",
        what: "Sarah uploads a discharge summary or lab; Memory Lane extracts diagnoses, meds, and appointments as structured facts she approves into the record. Every access is logged (HIPAA §164.312(b) audit trail).",
        caregiver: "A photo of a document becomes organized, usable information.",
        clinician: "Source documents plus structured facts — reviewable and trustworthy.",
        org: "Structured data instead of PDFs in a drawer; audit-ready by design.",
        patient: "Nothing about her care gets lost in a folder again.",
    },
    {
        path: "/care-summary", title: "Care summary — one link for any provider",
        what: "Sarah picks the sections and time window and gets ONE secure, expiring link plus a clinician-formatted PDF. Try it: send it to Maggie's neurologist Dr. McSteamy before the quarterly visit, or a limited version to Sarah's brother Michael — each share is separate, revocable, and audited.",
        caregiver: "'Want to share with Dr. McSteamy?' becomes a 10-second yes — family included.",
        clinician: "A pre-visit brief that fits in 90 seconds of reading.",
        org: "Standardized handoffs between families, clinics, and services.",
        patient: "Every new provider — and every helping relative — starts informed.",
    },
    {
        path: "/care-team", title: "Access control — Sarah decides who sees what",
        what: "Maggie's circle in one place: Dr. McSteamy (neurology), Dr. Patel (primary care), Dr. Lin (cardiology) — and family, like Sarah's brother Michael, who sees only the timeline and appointments. Sarah grants, limits, or revokes each person's access, and every view lands in an append-only access history (HIPAA audit controls).",
        caregiver: "Full view for the neurologist, limited view for family, revoke anyone in one tap.",
        clinician: "Consent is explicit and provable, so they can act on the data with confidence.",
        org: "Compliance-grade audit trail out of the box — passes security review.",
        patient: "Her private story is shared on her family's terms, never by default.",
    },
    {
        path: "/guide", title: "GUIDE — plugged into Medicare's dementia model",
        what: "Memory Lane walks families through eligibility for CMS's GUIDE Model (Medicare's dementia care program: care navigation, 24/7 support line, caregiver training, respite dollars) and produces a read-only summary link to hand a GUIDE provider.",
        caregiver: "Unlocks funded support most families never knew existed.",
        clinician: "GUIDE providers receive pre-qualified, pre-documented families.",
        org: "Positions Memory Lane inside a CMS payment model — reimbursable, not just subscription.",
        patient: "Access to funded care navigation and respite she's entitled to.",
    },
    {
        path: "/chat", title: "Lane — the 24/7 care companion",
        what: "Ask anything at 2am: 'Mom is accusing me of stealing — what do I do?' Lane answers with dementia-specific guidance grounded in Maggie's actual context.",
        caregiver: "Expert-level support at the exact moment of crisis.",
        clinician: "Fewer panicked calls; better-prepared questions at visits.",
        org: "Scalable support between human touchpoints.",
        patient: "Hard moments get de-escalated with the right response, not a guess.",
    },
    {
        path: "/burnout", title: "Caregiver wellbeing — burnout & wellness support",
        what: "Memory Lane checks on the caregiver too: burnout self-checks, mood tracking, and gentle interventions — because 40-70% of dementia caregivers show clinical depression symptoms.",
        caregiver: "Someone finally asks how SHE is doing — and catches the slide early.",
        clinician: "Caregiver collapse is the #1 driver of avoidable institutionalization; this is upstream prevention.",
        org: "Keeping the caregiver healthy keeps the family in the program (and out of the ER).",
        patient: "Maggie's care depends on Sarah staying well — this protects them both.",
    },
    {
        path: "/education", title: "Learn — education that meets the moment",
        what: "Weekly, stage-matched education: short lessons on what Sarah is facing right now — behaviors, communication, safety — written by clinicians, not content farms.",
        caregiver: "Learns exactly what she needs this week, in minutes not textbooks.",
        clinician: "Families show up educated — visits go deeper than 'what is sundowning?'",
        org: "Structured family education is a service line they can point to (and bill against in programs like GUIDE).",
        patient: "A caregiver who understands the disease responds with skill instead of fear.",
    },
    {
        path: "/resources", title: "Resources — vetted help, not a link dump",
        what: "Curated articles, local community partners, financial guidance ('who pays for what'), and eligibility help — matched to the family's stage and situation.",
        caregiver: "Answers from a trusted source instead of 40 contradictory search results.",
        clinician: "Somewhere reliable to send families beyond the 15-minute visit.",
        org: "Partner directory placement is a natural B2B surface.",
        patient: "Her family finds the right help — respite, day programs, benefits — sooner.",
    },
    {
        path: "/caregiver-tools", title: "Caregiver tools — the growth engine",
        what: "The public '30 Days of Things Nobody Tells You About Dementia Care' library: free printables shared daily on social, each pairing with its living digital version inside the product.",
        caregiver: "Immediate free value — no signup wall.",
        clinician: "Patients' families arrive with better records and fewer blind spots.",
        org: "A repeatable top-of-funnel: content → tool → account → activated caregiver.",
        patient: "More prepared caregivers, from day one of the diagnosis.",
    },
    {
        path: "/investor-demo/organization", title: "The organization view",
        what: "Memory care networks, home-care agencies, and clinics enroll families, see engagement and risk signals across their population, and co-brand the experience.",
        caregiver: "Their care program finally 'knows' what's happening at home.",
        clinician: "Population-level signals surface the families who need outreach now.",
        org: "Retention, differentiation, and B2B revenue for Memory Lane.",
        patient: "The whole circle of care — family, clinic, organization — works from one picture.",
    },
];

const AUDIENCES = [
    ["caregiver", "Caregiver", HeartHandshake],
    ["clinician", "Clinician", Stethoscope],
    ["org", "Organization", Building2],
    ["patient", "Person living with dementia", Brain],
];

export default function DemoGuide() {
    const location = useLocation();
    const navigate = useNavigate();
    const [active, setActive] = useState(() => localStorage.getItem("ml_demo_mode") === "1");
    const [open, setOpen] = useState(true);

    useEffect(() => {
        setActive(localStorage.getItem("ml_demo_mode") === "1");
    }, [location.pathname]);

    if (!active) return null;
    const idx = DEMO_STOPS.findIndex(s => location.pathname === s.path || location.pathname.startsWith(s.path + "/"));
    const stop = idx >= 0 ? DEMO_STOPS[idx] : null;

    const exitDemo = () => {
        ["ml_demo_mode", "ml_demo_step", "mlcn_session_id"].forEach(k => localStorage.removeItem(k));
        setToken(null);
        window.location.href = "/investor-demo";
    };

    return (
        <>
            <div className="fixed top-0 inset-x-0 z-[70] bg-[#1F2624] text-white text-xs sm:text-sm px-4 py-1.5 flex items-center justify-center gap-3" data-testid="demo-banner">
                <span>Investor demo — Sarah &amp; Margaret Chen are fictional; every feature is real.</span>
                <button onClick={exitDemo} className="underline underline-offset-2 hover:text-teal-soft" data-testid="demo-exit">Exit demo</button>
            </div>
            {stop && (
                <div className="fixed bottom-4 right-4 z-[70] w-[340px] max-w-[92vw]" data-testid="demo-guide-card">
                    <div className="rounded-2xl border border-teal/30 bg-white shadow-xl overflow-hidden">
                        <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5 bg-teal-dark text-white" data-testid="demo-guide-toggle">
                            <span className="text-xs font-semibold tracking-wide">DEMO GUIDE · Stop {idx + 1} of {DEMO_STOPS.length}</span>
                            {open ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
                        </button>
                        {open && (
                            <div className="p-4">
                                <p className="font-heading font-semibold text-[15px] text-[#12776F]">{stop.title}</p>
                                <p className="text-[12.5px] text-[#4C5551] mt-1.5 leading-relaxed">{stop.what}</p>
                                <div className="mt-3 space-y-1.5">
                                    {AUDIENCES.map(([k, label, Icon]) => (
                                        <p key={k} className="text-[11.5px] text-[#4C5551] flex items-start gap-1.5">
                                            <Icon size={13} className="text-teal-dark mt-[1px] shrink-0" />
                                            <span><strong className="text-[#1F2624]">{label}:</strong> {stop[k]}</span>
                                        </p>
                                    ))}
                                </div>
                                <div className="mt-3.5 flex items-center justify-between">
                                    <button disabled={idx <= 0} onClick={() => navigate(DEMO_STOPS[idx - 1].path)}
                                        className="text-xs text-[#6E7A74] inline-flex items-center gap-1 disabled:opacity-30" data-testid="demo-guide-back">
                                        <ArrowLeft size={13} /> Back
                                    </button>
                                    {idx < DEMO_STOPS.length - 1 ? (
                                        <button onClick={() => navigate(DEMO_STOPS[idx + 1].path)}
                                            className="ml-btn-primary !min-h-[34px] !px-4 !text-xs" data-testid="demo-guide-next">
                                            Next stop <ArrowRight size={13} />
                                        </button>
                                    ) : (
                                        <button onClick={exitDemo} className="ml-btn-primary !min-h-[34px] !px-4 !text-xs" data-testid="demo-guide-finish">
                                            Finish demo <X size={13} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {!stop && (
                <div className="fixed bottom-4 right-4 z-[70]" data-testid="demo-guide-resume">
                    <button onClick={() => navigate(DEMO_STOPS[0].path)} className="ml-btn-primary !min-h-[38px] !px-4 text-xs shadow-xl">
                        Resume the guided tour <ArrowRight size={13} />
                    </button>
                </div>
            )}
        </>
    );
}
